import React, { useState } from 'react';
import { Phone, Copy, CheckCircle } from 'lucide-react';

const PaymentModal = ({
    planType,
    onConfirm,
    onCancel
}: {
    planType: 'basic' | 'premium';
    onConfirm: (data: { senderNumber: string; transactionId: string; amount: number }) => void;
    onCancel: () => void;
}) => {
    const [senderNumber, setSenderNumber] = useState('');
    const [transactionId, setTransactionId] = useState('');
    const [amount, setAmount] = useState(planType === 'basic' ? 50 : 100);
    const vodafoneCashNumber = '01024973697';
    const [copied, setCopied] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false); // الحالة الجديدة

    const copyNumber = () => {
        navigator.clipboard.writeText(vodafoneCashNumber);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
            <div className="bg-gradient-to-br from-indigo-900 to-purple-900 rounded-xl max-w-md w-full p-8 my-auto max-h-[90vh] md:max-h-none overflow-y-auto custom-scrollbar">

                {!isSuccess ? (
                    // --- الحالة الأولى: فورم الدفع ---
                    <>
                        <div className="text-center mb-6">
                            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-500/20 rounded-full mb-4">
                                <Phone className="w-8 h-8 text-green-400" />
                            </div>
                            <h3 className="text-2xl font-bold mb-2">الدفع بفودافون كاش</h3>
                            <p className="text-purple-200">Vodafone Cash Payment</p>
                        </div>

                        <div className="bg-white/10 rounded-lg p-6 mb-6 space-y-4">
                            <div>
                                <p className="text-sm text-purple-300 mb-1">المبلغ المطلوب / Amount</p>
                                <p className="text-3xl font-bold text-yellow-300">{amount} جنيه</p>
                            </div>
                            
                            <div>
                                <p className="text-sm text-purple-300 mb-2">رقم فودافون كاش / Vodafone Cash Number</p>
                                <div className="flex gap-2 w-full items-center">
                                    <input
                                        type="text"
                                        value={vodafoneCashNumber}
                                        readOnly
                                        className="flex-1 min-w-0 px-3 py-2 rounded-lg bg-white/20 border border-white/30 text-white font-mono text-sm"
                                    />
                                    <button
                                        onClick={copyNumber}
                                        className="flex-shrink-0 px-4 py-2 rounded-lg bg-yellow-400 hover:bg-yellow-500 text-indigo-950 font-bold transition-all"
                                    >
                                        {copied ? <CheckCircle className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                                    </button>
                                </div>
                            </div>

                            <div className="bg-yellow-400/10 border border-yellow-400/30 rounded-lg p-4">
                                <p className="text-sm text-yellow-200 font-bold mb-2">خطوات الدفع:</p>
                                <ol className="text-sm text-purple-200 space-y-1 list-decimal list-inside">
                                    <li>افتح تطبيق فودافون كاش</li>
                                    <li>اختر "تحويل فلوس"</li>
                                    <li>حول {amount} جنيه للرقم المكتوب فوق</li>
                                    <li>احتفظ برقم العملية ورقمك</li>
                                </ol>
                            </div>
                        </div>

                        <div className="mb-6 space-y-4">
                            <div>
                                <label className="block text-sm mb-2">رقمك اللي حولت منه / Your Number</label>
                                <input
                                    type="text"
                                    value={senderNumber}
                                    onChange={(e) => setSenderNumber(e.target.value)}
                                    placeholder="مثال: 01012345678"
                                    className="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white placeholder-white/50 focus:outline-none focus:border-yellow-400"
                                />
                            </div>
                            <div>
                                <label className="block text-sm mb-2">رقم العملية / Transaction ID</label>
                                <input
                                    type="text"
                                    value={transactionId}
                                    onChange={(e) => setTransactionId(e.target.value)}
                                    placeholder="مثال: 123456789"
                                    className="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white placeholder-white/50 focus:outline-none focus:border-yellow-400"
                                />
                            </div>
                            <div>
                                <label className="block text-sm mb-2">المبلغ المحول / Amount Sent</label>
                                <input
                                    type="number"
                                    value={amount}
                                    onChange={(e) => setAmount(Number(e.target.value))}
                                    className="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white focus:outline-none focus:border-yellow-400"
                                />
                            </div>
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={onCancel}
                                className="flex-1 px-6 py-3 rounded-lg bg-white/10 hover:bg-white/20 text-white font-bold transition-all"
                            >
                                إلغاء
                            </button>
                            <button
                                onClick={() => {
                                    onConfirm({ senderNumber, transactionId, amount });
                                    setIsSuccess(true); // تفعيل حالة النجاح
                                }}
                                disabled={!senderNumber || !transactionId || amount <= 0}
                                className="flex-1 px-6 py-3 rounded-lg bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                تأكيد الدفع
                            </button>
                        </div>
                    </>
                ) : (
                    // --- الحالة الثانية: رسالة النجاح وكود التتبع ---
                    <div className="text-center py-6">
                        <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                            <CheckCircle className="w-12 h-12 text-green-400" />
                        </div>
                        <h3 className="text-2xl font-bold mb-4">تم إرسال الطلب!</h3>
                        <p className="text-purple-200 mb-6">احتفظ برقم العملية (ID) لمتابعة حالة طلبك من الصفحة الرئيسية.</p>

                        <div className="bg-white/10 p-6 rounded-xl border border-white/20 mb-8">
                            <p className="text-sm text-purple-300 mb-2">رقم العملية الخاص بك:</p>
                            <p className="text-2xl font-mono font-bold text-yellow-300 tracking-wider">
                                {transactionId}
                            </p>
                        </div>

                        <button
                            onClick={onCancel}
                            className="w-full py-4 rounded-xl bg-yellow-400 hover:bg-yellow-500 text-indigo-950 font-bold text-lg transition-all"
                        >
                            العودة للرئيسية
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PaymentModal;